import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getInvoice, 
  createPayment, 
  deletePayment, 
  createCreditNote, 
  deleteCreditNote,
  downloadInvoicePdf,
  downloadCreditNotePdf,
  updateInvoice,
  getQrBillStatus,
  sendInvoiceEmail,
  getSettings
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Download,
  Plus,
  Trash2,
  Receipt,
  CreditCard,
  FileText,
  Calendar,
  User,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Ban,
  Eye,
  QrCode,
  Mail,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [qrBillReady, setQrBillReady] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', to_name: '', subject: '', body_text: '' });
  
  // Payment form
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // Credit note form
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditNotes, setCreditNotes] = useState('');

  useEffect(() => {
    loadInvoice();
    checkQrBill();
  }, [id]);

  const checkQrBill = async () => {
    try {
      const [qrRes, settingsRes] = await Promise.all([getQrBillStatus(), getSettings()]);
      setQrBillReady(qrRes.data.configured);
      setSmtpConfigured(!!settingsRes.data.smtp_configured);
    } catch (err) { console.error('QR image not available:', err); }
  };

  const handleOpenEmailDialog = () => {
    if (!smtpConfigured) {
      toast.error('Configurez votre SMTP dans Parametres > Email pour envoyer directement.');
      return;
    }
    const clientEmail = invoice?.client?.email || '';
    const clientName = invoice?.client?.name || '';
    setEmailForm({
      to_email: clientEmail,
      to_name: clientName,
      subject: `Facture ${invoice?.invoice_number} - ${invoice?.total?.toFixed(2)} ${invoice?.currency || 'CHF'}`,
      body_text: `Bonjour ${clientName},\n\nVeuillez trouver ci-joint la facture ${invoice?.invoice_number} d'un montant de ${invoice?.total?.toFixed(2)} ${invoice?.currency || 'CHF'}.\n\nCordialement`
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmailSubmit = async () => {
    if (!emailForm.to_email) { toast.error('Veuillez saisir une adresse email'); return; }
    setSendingEmail(true);
    try {
      await sendInvoiceEmail(id, {
        to_email: emailForm.to_email,
        to_name: emailForm.to_name,
        subject: emailForm.subject,
        body_text: emailForm.body_text,
        document_id: id
      });
      toast.success('Facture envoyee par email !');
      setEmailDialogOpen(false);
      loadInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setSendingEmail(false); }
  };

  const loadInvoice = async () => {
    try {
      const response = await getInvoice(id);
      setInvoice(response.data);
      setPaymentAmount(response.data.amount_due.toFixed(2));
      setCreditAmount(response.data.amount_due.toFixed(2));
    } catch (error) {
      toast.error('Erreur lors du chargement de la facture');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await downloadInvoicePdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreviewPdf = async () => {
    setLoadingPreview(true);
    try {
      const response = await downloadInvoicePdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    
    setSubmitting(true);
    try {
      await createPayment({
        invoice_id: id,
        amount: parseFloat(paymentAmount),
        payment_date: paymentDate,
        payment_method: paymentMethod,
        reference: paymentReference || null,
        notes: paymentNotes || null
      });
      toast.success('Paiement enregistré');
      setPaymentDialogOpen(false);
      resetPaymentForm();
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    
    try {
      await deletePayment(paymentId);
      toast.success('Paiement supprimé');
      loadInvoice();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddCreditNote = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!creditReason.trim()) {
      toast.error('Veuillez indiquer un motif');
      return;
    }
    
    setSubmitting(true);
    try {
      await createCreditNote({
        invoice_id: id,
        amount: parseFloat(creditAmount),
        reason: creditReason,
        notes: creditNotes || null
      });
      toast.success('Avoir créé');
      setCreditDialogOpen(false);
      resetCreditForm();
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCreditNote = async (creditNoteId) => {
    if (!window.confirm('Supprimer cet avoir ?')) return;
    
    try {
      await deleteCreditNote(creditNoteId);
      toast.success('Avoir supprimé');
      loadInvoice();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await updateInvoice(id, { status: 'paid' });
      toast.success('Facture marquée comme payée');
      loadInvoice();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(invoice?.amount_due?.toFixed(2) || '');
    setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMethod('bank_transfer');
    setPaymentReference('');
    setPaymentNotes('');
  };

  const resetCreditForm = () => {
    setCreditAmount(invoice?.amount_due?.toFixed(2) || '');
    setCreditReason('');
    setCreditNotes('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', variant: 'secondary', icon: FileText },
      sent: { label: 'Envoyée', variant: 'default', icon: Clock },
      partial: { label: 'Partiellement payée', variant: 'warning', icon: CreditCard },
      paid: { label: 'Payée', variant: 'success', icon: CheckCircle },
      overdue: { label: 'En retard', variant: 'destructive', icon: AlertCircle },
      cancelled: { label: 'Annulée', variant: 'secondary', icon: Ban }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const paymentMethods = {
    bank_transfer: 'Virement bancaire',
    cash: 'Espèces',
    card: 'Carte bancaire',
    check: 'Chèque',
    paypal: 'PayPal',
    twint: 'TWINT',
    other: 'Autre'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Facture non trouvée</p>
        <Button onClick={() => navigate('/app/invoices')} className="mt-4">
          Retour aux factures
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/invoices')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Facture {invoice.invoice_number}
            </h1>
            <p className="text-slate-500">
              {invoice.client?.name || 'Client inconnu'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {getStatusBadge(invoice.status)}
          <Button variant="outline" onClick={handleOpenEmailDialog} data-testid="send-email-btn" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
            <Mail className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Envoyer</span>
          </Button>
          <Button variant="outline" onClick={handlePreviewPdf} disabled={loadingPreview} data-testid="preview-pdf-btn">
            {loadingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">Aperçu</span>
          </Button>
          <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          {qrBillReady && (
            <Button variant="outline" onClick={async () => {
              try {
                const response = await downloadInvoicePdf(id, true);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${invoice.invoice_number}_QR.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('PDF avec QR-facture suisse');
              } catch { toast.error('Erreur QR-facture'); }
            }} data-testid="download-qr-pdf-btn" className="border-blue-200 text-blue-700 hover:bg-blue-50">
              <QrCode className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">QR-Facture</span>
            </Button>
          )}
          <Link to={`/app/invoices/${id}/edit`}>
            <Button variant="outline">
              <span className="hidden sm:inline">Modifier</span>
              <span className="sm:hidden">Edit</span>
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Détails de la facture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium">
                    {invoice.invoice_date 
                      ? format(new Date(invoice.invoice_date), 'dd/MM/yyyy')
                      : format(new Date(invoice.created_at), 'dd/MM/yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Échéance</p>
                  <p className="font-medium">
                    {invoice.due_date ? format(new Date(invoice.due_date), 'dd/MM/yyyy') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Montant total</p>
                  <p className="font-bold text-lg">{invoice.total.toFixed(2)} {invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">TVA</p>
                  <p className="font-medium">{invoice.vat_enabled ? `${invoice.vat_total.toFixed(2)} ${invoice.currency}` : 'Exonéré'}</p>
                </div>
              </div>

              {/* Items */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Articles</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3">Description</th>
                        <th className="text-right p-3">Qté</th>
                        <th className="text-right p-3">Prix unit.</th>
                        <th className="text-right p-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items?.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.description}</td>
                          <td className="text-right p-3">{item.quantity}</td>
                          <td className="text-right p-3">{item.unit_price.toFixed(2)}</td>
                          <td className="text-right p-3 font-medium">
                            {(item.quantity * item.unit_price).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Paiements reçus
              </CardTitle>
              {invoice.amount_due > 0 && invoice.status !== 'cancelled' && (
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-payment-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Enregistrer un paiement
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Enregistrer un paiement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Montant ({invoice.currency})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="0.00"
                          data-testid="payment-amount-input"
                        />
                      </div>
                      <div>
                        <Label>Date du paiement</Label>
                        <Input
                          type="date"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          data-testid="payment-date-input"
                        />
                      </div>
                      <div>
                        <Label>Méthode de paiement</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger data-testid="payment-method-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(paymentMethods).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Référence (optionnel)</Label>
                        <Input
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          placeholder="N° de transaction..."
                          data-testid="payment-reference-input"
                        />
                      </div>
                      <div>
                        <Label>Notes (optionnel)</Label>
                        <Textarea
                          value={paymentNotes}
                          onChange={(e) => setPaymentNotes(e.target.value)}
                          placeholder="Notes..."
                          rows={2}
                        />
                      </div>
                      <Button 
                        onClick={handleAddPayment} 
                        className="w-full"
                        disabled={submitting}
                        data-testid="confirm-payment-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Enregistrer
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {invoice.payments && invoice.payments.length > 0 ? (
                <div className="space-y-3">
                  {invoice.payments.map((payment) => (
                    <div 
                      key={payment.id} 
                      className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-900">
                            {payment.amount.toFixed(2)} {invoice.currency}
                          </p>
                          <p className="text-sm text-green-700">
                            {paymentMethods[payment.payment_method] || payment.payment_method} • {format(new Date(payment.payment_date), 'dd/MM/yyyy')}
                            {payment.reference && ` • Réf: ${payment.reference}`}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeletePayment(payment.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Aucun paiement enregistré</p>
              )}
            </CardContent>
          </Card>

          {/* Credit Notes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Avoirs
              </CardTitle>
              {invoice.amount_due > 0 && invoice.status !== 'cancelled' && (
                <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="add-credit-note-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Créer un avoir
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Créer un avoir</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Montant ({invoice.currency})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          placeholder="0.00"
                          data-testid="credit-amount-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Maximum: {invoice.amount_due.toFixed(2)} {invoice.currency}
                        </p>
                      </div>
                      <div>
                        <Label>Motif de l'avoir *</Label>
                        <Select value={creditReason} onValueChange={setCreditReason}>
                          <SelectTrigger data-testid="credit-reason-select">
                            <SelectValue placeholder="Sélectionner un motif" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Annulation de commande">Annulation de commande</SelectItem>
                            <SelectItem value="Retour de marchandise">Retour de marchandise</SelectItem>
                            <SelectItem value="Erreur de facturation">Erreur de facturation</SelectItem>
                            <SelectItem value="Geste commercial">Geste commercial</SelectItem>
                            <SelectItem value="Remise accordée">Remise accordée</SelectItem>
                            <SelectItem value="Autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Notes (optionnel)</Label>
                        <Textarea
                          value={creditNotes}
                          onChange={(e) => setCreditNotes(e.target.value)}
                          placeholder="Détails supplémentaires..."
                          rows={2}
                        />
                      </div>
                      <Button 
                        onClick={handleAddCreditNote} 
                        className="w-full"
                        disabled={submitting}
                        data-testid="confirm-credit-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Créer l'avoir
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {invoice.credit_notes && invoice.credit_notes.length > 0 ? (
                <div className="space-y-3">
                  {invoice.credit_notes.map((cn) => (
                    <div 
                      key={cn.id} 
                      className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100"
                    >
                      <div className="flex items-center gap-3">
                        <Receipt className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-900">
                            -{cn.amount.toFixed(2)} {invoice.currency}
                          </p>
                          <p className="text-sm text-orange-700">
                            {cn.credit_number} • {cn.reason} • {format(new Date(cn.credit_date), 'dd/MM/yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={async () => {
                            try {
                              const response = await downloadCreditNotePdf(cn.id);
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `${cn.credit_number}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                            } catch (error) {
                              toast.error('Erreur lors du téléchargement');
                            }
                          }}
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                          title="Télécharger PDF"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteCreditNote(cn.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Aucun avoir</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Balance Summary */}
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="text-lg">Résumé financier</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-600">Total facture</span>
                <span className="font-medium">{invoice.total.toFixed(2)} {invoice.currency}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Paiements reçus</span>
                <span>-{invoice.amount_paid.toFixed(2)} {invoice.currency}</span>
              </div>
              {invoice.amount_credited > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>Avoirs</span>
                  <span>-{invoice.amount_credited.toFixed(2)} {invoice.currency}</span>
                </div>
              )}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg">
                  <span className="font-semibold">Solde dû</span>
                  <span className={`font-bold ${invoice.amount_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {invoice.amount_due.toFixed(2)} {invoice.currency}
                  </span>
                </div>
              </div>
              
              {invoice.amount_due === 0 && invoice.status !== 'paid' && (
                <Button onClick={handleMarkAsPaid} className="w-full bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marquer comme payée
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoice.client ? (
                <div className="space-y-2">
                  <p className="font-medium">{invoice.client.name}</p>
                  {invoice.client.email && (
                    <p className="text-sm text-slate-500">{invoice.client.email}</p>
                  )}
                  {invoice.client.address && (
                    <p className="text-sm text-slate-500">{invoice.client.address}</p>
                  )}
                  <Link to={`/app/clients/${invoice.client_id}`}>
                    <Button variant="outline" size="sm" className="w-full mt-3">
                      Voir la fiche client
                    </Button>
                  </Link>
                </div>
              ) : (
                <p className="text-slate-500">Client non trouvé</p>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {(invoice.notes || invoice.internal_notes) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {invoice.notes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Note client</p>
                    <p className="text-sm">{invoice.notes}</p>
                  </div>
                )}
                {invoice.internal_notes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Note interne</p>
                    <p className="text-sm text-slate-600 italic">{invoice.internal_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Envoyer la facture par email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email destinataire *</Label>
                <Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="client@example.com" data-testid="email-to-input" />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={emailForm.to_name} onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })} placeholder="Nom du client" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject-input" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={emailForm.body_text} onChange={(e) => setEmailForm({ ...emailForm, body_text: e.target.value })} rows={5} data-testid="email-body-input" />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">Le PDF de la facture sera automatiquement joint.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSendEmailSubmit} disabled={sendingEmail} className="bg-emerald-600 hover:bg-emerald-700" data-testid="email-send-btn">
              {sendingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</> : <><Send className="w-4 h-4 mr-2" />Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={(open) => {
        if (!open && previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        setPreviewOpen(open);
      }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center justify-between">
              <span>Aperçu - {invoice?.invoice_number}</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleDownloadPdf}>
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-[calc(90vh-80px)]"
                title="Aperçu PDF"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceDetailPage;
